import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';

const SCENE_IMAGES = {
  morning: 'https://static.prod-images.emergentagent.com/jobs/703ba438-d4e3-4e30-afca-0655aaab9328/images/3924d4006795e2f2cec2a951b393ea068aa7a47cceb139e85d1add5f4544f39c.png',
  midday: 'https://static.prod-images.emergentagent.com/jobs/703ba438-d4e3-4e30-afca-0655aaab9328/images/85c070d727fd111d1af49ff915b0e4f7c6cd0fd6ad8fd1248603dcb7e1cc43ea.png',
  evening: 'https://static.prod-images.emergentagent.com/jobs/703ba438-d4e3-4e30-afca-0655aaab9328/images/c0494f4d00acfd1bdb705e6a9b46419f0b85e2e5220ce81a5929ea634a942ba8.png',
  night: 'https://static.prod-images.emergentagent.com/jobs/703ba438-d4e3-4e30-afca-0655aaab9328/images/613abfc3c91ef11a5f5072c1866215a221596e652423a6582dcd100cad5e13fa.png',
};

/* ---- Stars (night) ---- */
function Stars() {
  const stars = useMemo(() =>
    Array.from({ length: 22 }, (_, i) => ({
      id: i,
      x: 5 + (i * 37 + i * i * 13) % 90,
      y: 3 + (i * 23 + i * 7) % 40,
      size: 1 + (i % 3),
      delay: (i * 0.7) % 4,
      dur: 2 + (i % 3),
    })), []);

  return (
    <div className="scene-layer" data-testid="scene-stars">
      {stars.map(s => (
        <div
          key={s.id}
          className="scene-star"
          style={{
            left: `${s.x}%`,
            top: `${s.y}%`,
            width: s.size,
            height: s.size,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.dur}s`,
          }}
        />
      ))}
    </div>
  );
}

/* ---- Birds (morning / midday) ---- */
function Birds() {
  const birds = useMemo(() => [
    { id: 0, y: 12, size: 6, dur: 18, delay: 0 },
    { id: 1, y: 18, size: 5, dur: 22, delay: 4 },
    { id: 2, y: 8,  size: 4, dur: 25, delay: 9 },
    { id: 3, y: 22, size: 5, dur: 20, delay: 14 },
  ], []);

  return (
    <div className="scene-layer" data-testid="scene-birds">
      {birds.map(b => (
        <svg
          key={b.id}
          className="scene-bird"
          viewBox="0 0 20 8"
          style={{
            top: `${b.y}%`,
            width: b.size,
            animationDuration: `${b.dur}s`,
            animationDelay: `${b.delay}s`,
          }}
        >
          <path d="M0 4 Q5 0 10 4 Q15 0 20 4" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      ))}
    </div>
  );
}

/* ---- Clouds (midday / morning) ---- */
function Clouds() {
  const clouds = useMemo(() => [
    { id: 0, y: 6,  w: 60, h: 18, dur: 80, delay: 0,  opacity: 0.25 },
    { id: 1, y: 14, w: 45, h: 14, dur: 100, delay: 20, opacity: 0.18 },
    { id: 2, y: 3,  w: 35, h: 12, dur: 90,  delay: 40, opacity: 0.15 },
  ], []);

  return (
    <div className="scene-layer" data-testid="scene-clouds">
      {clouds.map(c => (
        <div
          key={c.id}
          className="scene-cloud"
          style={{
            top: `${c.y}%`,
            width: c.w,
            height: c.h,
            opacity: c.opacity,
            animationDuration: `${c.dur}s`,
            animationDelay: `${c.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

/* ---- Ocean shimmer (all times) ---- */
function OceanShimmer({ isDark }) {
  return (
    <div className="scene-layer" data-testid="scene-shimmer">
      <div
        className="scene-shimmer"
        style={{ opacity: isDark ? 0.12 : 0.18 }}
      />
      <div
        className="scene-shimmer scene-shimmer-2"
        style={{ opacity: isDark ? 0.08 : 0.12 }}
      />
    </div>
  );
}

export default function BeachScene() {
  const { timeOfDay, isDark } = useTheme();
  const src = SCENE_IMAGES[timeOfDay] || SCENE_IMAGES.midday;
  const isNight = timeOfDay === 'night';
  const isDay = timeOfDay === 'morning' || timeOfDay === 'midday';

  return (
    <div
      className="relative w-full overflow-hidden rounded-2xl"
      style={{ aspectRatio: '3 / 2' }}
      data-testid="beach-scene"
    >
      <AnimatePresence mode="wait">
        <motion.img
          key={timeOfDay}
          src={src}
          alt={`Beach scene — ${timeOfDay}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
      </AnimatePresence>

      {/* Animated overlays */}
      {isNight && <Stars />}
      {isDay && <Birds />}
      {isDay && <Clouds />}
      <OceanShimmer isDark={isDark} />

      {/* Bottom gradient fade into card background */}
      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-bg-paper/95 to-transparent pointer-events-none" />
    </div>
  );
}
